'use client';
import dynamic from 'next/dynamic';
import cn from 'classnames';
import styles from './stickyHeaderFooterCta.module.scss';
import Button, { ButtonProps } from 'de-sxm-reactlib-2/components/atoms/Button/Button';
const Text = dynamic(() => import('de-sxm-reactlib/components/atoms/Display/Text/Text'));
const Grid = dynamic(() => import('de-sxm-reactlib/components/atoms/Layout/Grid/Grid'));
import { RefObject, useEffect, useRef } from 'react';
import ImageWrapper from '@/shared-components/ImageWrapper/ImageWrapper';
import { StickyCta } from '@/components/atoms/Display';
import { StickyBox } from '@/components/atoms/Display';
import { Document } from '@contentful/rich-text-types';
import { useMediaQuery } from '@/hooks';
import { responsiveBreakpointsPx } from '@/constants';

interface CtaProps extends ButtonProps {
    qrCode?: {
        url: string;
        altText: string;
        width?: number;
        height?: number;
    };
    media?: {
        url: string;
        altText: string;
        width?: number;
        height?: number;
    };
    secondaryText?: Document;
}

export interface StickyBoxHeaderFooterCtaProps {
    planName?: React.ReactNode;
    highlight?: React.ReactNode;
    highlightColor?: string;
    crossedOutText?: string;
    details?: React.ReactNode;
    body?: React.ReactNode;
    promoMobileText?: React.ReactNode;
    phoneNumber?: string;
    phoneText?: React.ReactNode;
    ctaOption: 'text' | 'button';
    cta: CtaProps | CtaProps[];
    logoImageUrl?: string;
    triggerRef?: RefObject<HTMLElement | null>;
    alwaysVisibleInMobile?: boolean;
    alwaysVisibleInDesktop?: boolean;
    showTextInMobile?: boolean;
    isTextSmall?: boolean;
    borderColor?: string;
    stickyCta?: boolean;
    scrollStartPosition?: number;
    showSecondaryText?: boolean;
}
export default function StickyHeaderFooterCta({
    planName,
    highlight,
    highlightColor = 'black',
    crossedOutText,
    details,
    body,
    promoMobileText,
    phoneNumber,
    phoneText,
    ctaOption = 'text',
    cta,
    triggerRef,
    logoImageUrl,
    alwaysVisibleInMobile,
    alwaysVisibleInDesktop,
    showTextInMobile = false,
    isTextSmall = true,
    borderColor,
    stickyCta = false,
    scrollStartPosition,
    showSecondaryText = true,
}: StickyBoxHeaderFooterCtaProps) {
    const isViewportDesktop = useMediaQuery(`(min-width: ${responsiveBreakpointsPx.md}px)`);

    const renderCtas = () => {
        if (Array.isArray(cta)) {
            return cta.map((ctaItem, index) => {
                if ((ctaItem.secondaryText && showSecondaryText) || ctaItem.qrCode) {
                    return (
                        <StickyCta
                            key={index}
                            headerFooterBarVersion
                            qrCode={ctaItem.qrCode}
                            staticImage={ctaItem.media}
                            ctaText={<>{ctaItem.children}</>}
                            ctaUrl={ctaItem.href}
                            variant={ctaItem.variant}
                            buttonType={ctaItem.buttonType}
                        />
                    );
                }
                return (
                    <Button
                        key={index}
                        {...ctaItem}
                        variant={ctaOption === 'text' ? 'tertiary' : 'primary'}
                        tertiaryNoPadding={ctaOption === 'text'}
                        colorSet="base"
                    />
                );
            });
        }
        return (
            <Button
                {...cta}
                variant={ctaOption === 'text' ? 'tertiary' : 'primary'}
                tertiaryNoPadding={ctaOption === 'text'}
                colorSet="base"
            />
        );
    };
    const infoSectionClassNames = cn(styles.infoSection, {
        [styles.showInMobile]: showTextInMobile,
    });
    const wrapperClassNames = cn(styles.wrapper, {
        [styles.border]: !!borderColor,
    });
    const style: React.CSSProperties = {
        ...({ '--border-color': borderColor } as React.CSSProperties),
    };
    const stickyHeaderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const updateHeight = () => {
            if (stickyHeaderRef.current) {
                const height = stickyHeaderRef.current.clientHeight;
                if (height > 0) {
                    document.documentElement.style.setProperty('--sticky-footer-padding', `${height - 29}px`);
                } else {
                    timeoutId = setTimeout(updateHeight, 100);
                }
            }
        };

        updateHeight();

        const handleResize = () => {
            updateHeight();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            document.documentElement.style.setProperty('--sticky-footer-padding', '24px');
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    const containerStyles = cn(styles.container, {
        [styles.mobileStickyContainer]: stickyCta && !isViewportDesktop,
        [styles.stickyContainer]: stickyCta && isViewportDesktop,
    });

    return (
        <StickyBox
            triggerRef={triggerRef}
            alwaysVisibleInMobile={alwaysVisibleInMobile}
            alwaysVisibleInDesktop={alwaysVisibleInDesktop}
            scrollStartPosition={scrollStartPosition}
            className={!isViewportDesktop && stickyCta ? styles.mobileStickyCta : stickyCta ? styles.stickyCta : ''}
        >
            <div style={style} className={wrapperClassNames} ref={stickyHeaderRef}>
                <Grid container>
                    <Grid col={{ sm: 12 }}>
                        <div className={containerStyles}>
                            <div className={infoSectionClassNames}>
                                {logoImageUrl && (
                                    <ImageWrapper
                                        optimizedWidth={80}
                                        optimizedHeight={40}
                                        src={logoImageUrl}
                                        alt="Radio Logo"
                                    />
                                )}
                                <div className={styles.textSection}>
                                    {(planName || highlight) && (
                                        <div className={styles.titleBox}>
                                            {planName}
                                            {highlight && (
                                                <span
                                                    style={{
                                                        color: highlightColor,
                                                        marginLeft: highlightColor ? '12px' : '24px',
                                                    }}
                                                >
                                                    {highlight}
                                                </span>
                                            )}
                                            {crossedOutText && (
                                                <span style={{ marginLeft: '12px' }} className={styles.crossed}>
                                                    {crossedOutText}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {(details || body) && (
                                        <Text tag="p" variant={isTextSmall ? 'small' : undefined}>
                                            {details} {body}
                                        </Text>
                                    )}
                                </div>
                            </div>
                            {promoMobileText && <div className={styles.mobilePromo}>{promoMobileText}</div>}
                            <div className={styles.ctaWrapper} data-cy="molecules-sticky-header-footer-cta-cta-wrapper">
                                {renderCtas()}
                            </div>
                            {phoneNumber && (
                                <div className={styles.number}>
                                    {phoneText} <a href={phoneNumber}>{phoneNumber}</a>
                                </div>
                            )}
                        </div>
                    </Grid>
                </Grid>
            </div>
        </StickyBox>
    );
}
